import React from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
} from '@chakra-ui/react';
import DrawerHeaderBar from 'components/DrawerHeaderBar';
import { useScrimLayer } from 'lib/contexts/ScrimContext';

// Figma drawer chrome, shared with drawer-LIKE routed pages (e.g. the
// JobInfoERP current-job overview) so the numbers can never drift apart.
export const DRAWER_TOP_INSET = '1.5rem';
export const DRAWER_TOP_RADIUS = '0.5rem';
export const DRAWER_SHADOW =
  '0px 0px 1px 0px rgba(26, 32, 44, 0.2), 0px 26px 80px 0px rgba(26, 32, 44, 0.2)';

/**
 * A styled drawer component that matches the design system.
 * Slides up from the bottom with rounded corners, a header with close button,
 * and a body area for content.
 */
const MMDrawer = ({
  isOpen,
  onClose,
  title,
  headerLeft,
  hideHeader = false,
  closeAction = 'dismiss',
  ariaLabel,
  children,
}) => {
  const closeAriaLabel = closeAction === 'back' ? 'Back' : 'Close';
  // Stacked-modal rule (ScrimContext): only the bottom-most layer draws a
  // scrim. When another layer already dims the screen (e.g. the drawer-look
  // current-job page), this drawer's overlay goes transparent so opening it
  // doesn't darken the faded area further — while still catching outside
  // taps for dismiss.
  const scrimBeneath = useScrimLayer(isOpen);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="bottom" size="full">
      <DrawerOverlay bg={scrimBeneath ? 'transparent' : undefined} />
      <DrawerContent
        // Figma: drawers sit 24px below the top of the screen with 8px
        // rounded top corners (shared across all drawer surfaces).
        mt={DRAWER_TOP_INSET}
        borderTopRadius={DRAWER_TOP_RADIUS}
        maxH={`calc(100vh - ${DRAWER_TOP_INSET})`}
        // Clip children (e.g. a full-bleed embed iframe) to the rounded top —
        // without this the embed's own header paints square corners.
        overflow="hidden"
        // Gives the dialog an accessible name even when the header (and its
        // visible title) is hidden.
        aria-label={ariaLabel}
      >
        {!hideHeader && (
          <DrawerHeaderBar
            title={title}
            onClose={onClose}
            headerLeft={headerLeft}
            closeAriaLabel={closeAriaLabel}
          />
        )}
        <DrawerBody
          p={0}
          display="flex"
          flexDirection="column"
          position="relative"
        >
          {children}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

MMDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  headerLeft: PropTypes.node,
  hideHeader: PropTypes.bool,
  closeAction: PropTypes.oneOf(['dismiss', 'back']),
  /** Accessible name for the drawer dialog (needed when hideHeader). */
  ariaLabel: PropTypes.string,
  children: PropTypes.node,
};

export default MMDrawer;
